import type { EvmProvider } from "@/lib/mudwater";

/* "your connected EVM wallet", not a chain by name: this adapter is used on
   ApeChain, Sepolia and Ethereum mainnet, and the deployment's chain is read
   from the signed release rather than hard-coded. Naming ApeChain here told a
   Sepolia holder to connect a wallet the deployment does not use. The same
   rule the claim copy already follows (`claim-errors.ts`). */
const NOT_CONNECTED = "Connect your EVM wallet, then try again.";
const NOT_STANDARD = "This EVM wallet does not expose a standard provider.";

function isEip1193Provider(value: unknown): value is EvmProvider {
  return (
    typeof value === "object" &&
    value !== null &&
    "request" in value &&
    typeof value.request === "function"
  );
}

/**
 * A stable EIP-1193 facade over whichever connector wagmi currently holds.
 * `@mudwater/sdk` keeps this object for the life of a session and calls
 * `eth_chainId`, `personal_sign` and `eth_signTypedData_v4` through it, so the
 * connector is resolved per request rather than captured once — otherwise a
 * reconnect leaves the SDK talking to a dead provider.
 */
export function createEvmProvider(
  resolveProvider: () => Promise<unknown>
): EvmProvider {
  return {
    async request(args, beforeSend) {
      const provider = await resolveProvider();
      if (provider === undefined || provider === null)
        throw new Error(NOT_CONNECTED);
      if (!isEip1193Provider(provider)) throw new Error(NOT_STANDARD);
      // Provider resolution can outlive the initiating Glyph or EVM session.
      if (args.method === "eth_sendTransaction") beforeSend?.();
      return provider.request(args);
    },
  };
}
